import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Flame, Search, ChevronLeft, Building2, Loader2, Plus,
  CheckCircle, AlertCircle, Clock, AlertTriangle, X, Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import { todayEasternStr } from '@/utils/aarValidation';

// 2026-06-03 — Safe date formatter for YYYY-MM-DD strings.
// `new Date("2026-03-01")` parses as UTC midnight which becomes "2/28/2026"
// in Eastern via toLocaleDateString(), making saved dates look wrong. Strip
// any time component and format the wall-clock value verbatim so the date
// the user typed is the date the user sees.
const formatLogDate = (value) => {
  if (!value) return 'N/A';
  const dateOnly = String(value).split('T')[0];
  const m = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return dateOnly;
  return `${m[2]}/${m[3]}/${m[1]}`;
};

// Status color badge component
const StatusBadge = ({ color, daysRemaining, isActive = true }) => {
  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    yellow: 'bg-yellow-100 text-yellow-700',
    orange: 'bg-orange-100 text-orange-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-100 text-gray-600'
  };
  
  const icons = {
    green: <CheckCircle className="w-4 h-4" />,
    yellow: <Clock className="w-4 h-4" />,
    orange: <AlertTriangle className="w-4 h-4" />,
    red: <AlertCircle className="w-4 h-4" />,
    gray: <Clock className="w-4 h-4" />
  };
  
  // Bug E: Show "Inactive" badge for inactive welders
  if (!isActive) {
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
        <Clock className="w-4 h-4" />
        <span>Inactive</span>
      </div>
    );
  }
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${colorClasses[color] || colorClasses.gray}`}>
      {icons[color] || icons.gray}
      <span>{daysRemaining !== null ? `${daysRemaining} days` : 'N/A'}</span>
    </div>
  );
};

// Welder Detail Component (shows logs and allows adding new logs)
function WelderDetail({ welder, onBack, api, user, onWelderUpdate }) {
  const [logs, setLogs] = useState(welder?.maintenance_logs || []);
  const [welderData, setWelderData] = useState(welder); // Bug B fix: local state for welder data
  const [showNewLogForm, setShowNewLogForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateError, setDateError] = useState(''); // Rule A: Date validation error
  const [logForm, setLogForm] = useState({
    process: '',
    position: '',
    component_or_car_number: '',
    wps: '',
    date: todayEasternStr(),
    verified_by: ''
  });

  // Rule D: Get the most recent log by date (for Rule C validation)
  const getMostRecentLogByDate = () => {
    if (!logs || logs.length === 0) return null;
    // Logs are already sorted by date desc from backend
    return logs[0];
  };

  // 2026-06-03 — When editing the latest log, the editable lower bound is
  // the previous (second-newest) log's date so the maintenance cadence never
  // moves backwards. Returns a YYYY-MM-DD string or null if there is no
  // previous log.
  const getPreviousLogDateStr = () => {
    if (!logs || logs.length < 2) return null;
    const second = logs[1];
    if (!second?.date) return null;
    return String(second.date).split('T')[0];
  };

  // Rule C: Check if a log is the most recent (can edit date)
  const isLogMostRecent = (log) => {
    const mostRecent = getMostRecentLogByDate();
    return mostRecent && mostRecent.id === log.id;
  };

  const resetForm = () => {
    setLogForm({
      process: '',
      position: '',
      component_or_car_number: '',
      wps: '',
      date: todayEasternStr(),
      verified_by: ''
    });
    setEditingLog(null);
    setDateError('');
  };

  // 2026-06-03 — Maintenance log date validation:
  //   • Must not be in the future (US Eastern anchor).
  //   • When editing the LATEST log, must not be earlier than the previous
  //     log's date (preserves cadence).
  const validateDate = (dateStr) => {
    if (!dateStr) return true;
    if (dateStr > todayEasternStr()) {
      setDateError('Maintenance log date cannot be a future date.');
      return false;
    }
    if (editingLog && isLogMostRecent(editingLog)) {
      const prev = getPreviousLogDateStr();
      if (prev && dateStr < prev) {
        setDateError(
          `Date cannot be earlier than the previous log (${formatLogDate(prev)}).`
        );
        return false;
      }
    }
    setDateError('');
    return true;
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setLogForm(prev => ({ ...prev, date: newDate }));
    validateDate(newDate);
  };

  const handleSubmitLog = async (e) => {
    e.preventDefault();
    
    // Rule A: Frontend validation - block future dates
    if (!validateDate(logForm.date)) {
      return;
    }
    
    setLoading(true);
    
    try {
      if (editingLog) {
        // Rule C: When editing, only send date if it's the most recent log
        const payload = { ...logForm };
        if (!isLogMostRecent(editingLog)) {
          delete payload.date; // Don't send date for historical logs
        }
        await api.put(`/lms/welder-maintenance-logs/${editingLog.id}`, payload);
        toast.success('Maintenance log updated');
      } else {
        // Create new log
        await api.post(`/lms/welders/${welderData.technician_id}/maintenance-logs`, logForm);
        toast.success('Maintenance log created');
      }
      
      // Bug B fix: Refresh data and update local state
      const response = await api.get(`/lms/welders/${welderData.technician_id}`);
      const updatedWelder = response.data;
      setLogs(updatedWelder.maintenance_logs || []);
      setWelderData(updatedWelder); // Update local state to trigger re-render
      
      // Notify parent to refresh list view
      if (onWelderUpdate) {
        onWelderUpdate(updatedWelder);
      }
      
      setShowNewLogForm(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save maintenance log');
    } finally {
      setLoading(false);
    }
  };

  const handleEditLog = (log) => {
    setLogForm({
      process: log.process,
      position: log.position,
      component_or_car_number: log.component_or_car_number,
      wps: log.wps,
      date: log.date?.split('T')[0] || '',
      verified_by: log.verified_by
    });
    setEditingLog(log);
    setShowNewLogForm(true);
  };

  // Bug E: Check if welder is inactive
  const isInactive = welderData.is_active === false || welderData.active_flag === false;

  return (
    <div className="space-y-6">
      {/* Header — 2026-07-17: New Maintenance Log now lives here (top-right)
          instead of a separate row above the log history. */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-to-list">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900" data-testid="welder-name">
                  {welderData.technician_name}
                </h2>
                {isInactive && (
                  <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-200 text-gray-600">
                    Inactive
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                ID: {welderData.technician_employee_id} | {welderData.facility_name}
              </p>
            </div>
          </div>
          {!showNewLogForm && !isInactive && (
            <Button
              onClick={() => { resetForm(); setShowNewLogForm(true); }}
              data-testid="new-log-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Maintenance Log
            </Button>
          )}
        </div>
      </div>

      {/* 2026-07-14 — AAR per-process compliance strip. Compact table view
          showing due date per active welding process. */}
      {Array.isArray(welderData.process_compliance) && welderData.process_compliance.length > 0 && (() => {
        // Dedicated MM-DD-YYYY formatter (compliance-strip only) — the rest of
        // the page continues to use formatLogDate's MM/DD/YYYY style.
        const dashDate = (v) => {
          if (!v) return '—';
          const s = String(v).split('T')[0];
          const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          return m ? `${m[2]}-${m[3]}-${m[1]}` : s;
        };
        // 2026-07-17 — Status column output (unified — no separate Countdown column):
        //   • no due date              → "Not Set"     (gray badge)
        //   • due date in the past     → "Overdue"     (red badge)
        //   • due date in the future   → "N days"      (color per bucket)
        const todayStr = todayEasternStr();
        const daysBetween = (dueStr) => {
          if (!dueStr) return null;
          const s = String(dueStr).split('T')[0];
          const [y, m, d] = s.split('-').map(Number);
          const [ty, tm, td] = todayStr.split('-').map(Number);
          const dueUTC = Date.UTC(y, m - 1, d);
          const todayUTC = Date.UTC(ty, tm - 1, td);
          return Math.round((dueUTC - todayUTC) / (1000 * 60 * 60 * 24));
        };
        // Color mapping stays aligned with the previous bucket logic so the
        // existing red/orange/yellow/green cadence is preserved.
        const colorClassFor = (days) => {
          if (days === null) return 'bg-gray-100 text-gray-600 border-gray-200'; // Not Set
          if (days < 0) return 'bg-red-100 text-red-800 border-red-200';         // Overdue
          if (days <= 14) return 'bg-orange-100 text-orange-800 border-orange-200';
          if (days <= 45) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
          return 'bg-green-100 text-green-800 border-green-200';
        };
        const statusLabelFor = (days) => {
          if (days === null) return 'Not Set';
          if (days < 0) return 'Overdue';
          return `${days} day${days === 1 ? '' : 's'}`;
        };
        return (
        <div className="bg-white border rounded-lg" data-testid="process-compliance-strip">
          <div className="px-4 py-3 border-b bg-gray-50">
            <div className="text-sm font-semibold text-gray-800">Process Compliance</div>
            <div className="text-xs text-gray-500">Each active process must be maintained within 6 calendar months.</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b bg-white">
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 font-medium">Process</th>
                  <th className="px-4 py-2 font-medium">Source</th>
                  <th className="px-4 py-2 font-medium">Last Relevant Date</th>
                  <th className="px-4 py-2 font-medium">Due Date</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {welderData.process_compliance.map((row) => {
                  const days = daysBetween(row.due_date);
                  const label = statusLabelFor(days);
                  return (
                    <tr key={row.process} className="border-b last:border-b-0" data-testid={`proc-row-${row.process}`}>
                      <td className="px-4 py-2 font-medium text-gray-900">{row.process}</td>
                      <td className="px-4 py-2 text-gray-700">{row.source}</td>
                      <td className="px-4 py-2 text-gray-700">
                        {dashDate(row.last_relevant_date)}
                      </td>
                      <td className="px-4 py-2 text-gray-900 font-medium whitespace-nowrap">
                        {dashDate(row.due_date)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClassFor(days)}`}
                          data-testid={`proc-status-${row.process}`}
                        >
                          {label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        );
      })()}

      {/* Inactive welder message */}
      {isInactive && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          This welder is inactive. Re-enable welder qualifications in User Management to add new logs.
        </div>
      )}

      {/* New/Edit Log Form */}
      {showNewLogForm && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                {editingLog ? 'Edit Maintenance Log' : 'New Maintenance Log'}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowNewLogForm(false); resetForm(); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitLog} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label>Process *</Label>
                <select
                  value={logForm.process}
                  onChange={(e) => setLogForm(prev => ({ ...prev, process: e.target.value }))}
                  className="w-full h-10 px-3 border rounded-md text-sm"
                  required
                  data-testid="log-process"
                >
                  <option value="">Select process...</option>
                  {(welderData.available_processes || []).map(proc => (
                    <option key={proc} value={proc}>{proc}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Position *</Label>
                <select
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={logForm.position}
                  onChange={(e) => setLogForm(prev => ({ ...prev, position: e.target.value }))}
                  required
                  data-testid="log-position"
                >
                  <option value="">Select position</option>
                  {["1F","2F","3F","4F","1G","2G","3G","4G","5G","6G"].map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Component or Car Number *</Label>
                <Input
                  value={logForm.component_or_car_number}
                  onChange={(e) => setLogForm(prev => ({ ...prev, component_or_car_number: e.target.value }))}
                  placeholder="e.g., CAR-12345"
                  required
                  data-testid="log-component"
                />
              </div>
              <div>
                <Label>WPS *</Label>
                <Input
                  value={logForm.wps}
                  onChange={(e) => setLogForm(prev => ({ ...prev, wps: e.target.value }))}
                  placeholder="Welding Procedure Specification"
                  required
                  data-testid="log-wps"
                />
              </div>
              <div>
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={logForm.date}
                  onChange={handleDateChange}
                  required
                  disabled={editingLog && !isLogMostRecent(editingLog)}
                  max={todayEasternStr()}
                  min={
                    editingLog && isLogMostRecent(editingLog)
                      ? (getPreviousLogDateStr() || undefined)
                      : undefined
                  }
                  className={dateError ? 'border-red-500' : ''}
                  data-testid="log-date"
                />
                {dateError && (
                  <p className="text-red-500 text-xs mt-1">{dateError}</p>
                )}
                {editingLog && isLogMostRecent(editingLog) && getPreviousLogDateStr() && (
                  <p className="text-gray-500 text-xs mt-1">
                    Must be on or after the previous log ({formatLogDate(getPreviousLogDateStr())}).
                  </p>
                )}
                {editingLog && !isLogMostRecent(editingLog) && (
                  <p className="text-gray-500 text-xs mt-1">Date cannot be changed for historical logs.</p>
                )}
              </div>
              <div>
                <Label>Verified By *</Label>
                <Input
                  value={logForm.verified_by}
                  onChange={(e) => setLogForm(prev => ({ ...prev, verified_by: e.target.value }))}
                  placeholder="Name of verifier"
                  required
                  data-testid="log-verified-by"
                />
              </div>
              <div className="md:col-span-2 lg:col-span-3 flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => { setShowNewLogForm(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || !!dateError} data-testid="save-log-btn">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingLog ? 'Update Log' : 'Save Log'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Maintenance Log History */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Maintenance Log History</CardTitle>
        </CardHeader>
        <CardContent>
          {logs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No maintenance logs yet. {!isInactive && 'Add a new log to track welder maintenance.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Process</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Position</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Component/Car #</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">WPS</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Date</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Verified By</th>
                    {!isInactive && <th className="text-left py-3 px-2 font-medium text-gray-700">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-gray-50" data-testid={`maintenance-log-row-${log.id}`}>
                      <td className="py-3 px-2">{log.process}</td>
                      <td className="py-3 px-2">{log.position}</td>
                      <td className="py-3 px-2">{log.component_or_car_number}</td>
                      <td className="py-3 px-2">{log.wps}</td>
                      <td className="py-3 px-2" data-testid={`log-date-cell-${log.id}`}>
                        {formatLogDate(log.date)}
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <span>{log.verified_by}</span>
                          {log.source === 'AUTO_LOG' && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200"
                              title="Created automatically by the Welder Maintenance Auto Log when a BRC was closed."
                              data-testid={`auto-logged-badge-${log.id}`}
                            >
                              Auto Logged
                            </span>
                          )}
                        </div>
                      </td>
                      {!isInactive && (
                        <td className="py-3 px-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditLog(log)}
                            data-testid={`edit-log-${log.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Main Welder Maintenance Page
export default function WelderMaintenancePage() {
  const { api, user } = useAuth();
  const { technician_id } = useParams();
  const navigate = useNavigate();
  
  const [welders, setWelders] = useState([]);
  const [selectedWelder, setSelectedWelder] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // Bug E: status filter
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch facilities
        const facilitiesRes = await api.get('/lms/facilities');
        setFacilities(facilitiesRes.data || []);
        
        // Fetch welders with status filter (Bug E)
        let url = `/lms/welders?status_filter=${statusFilter}`;
        if (selectedFacility) {
          url += `&facility_id=${selectedFacility}`;
        }
        const weldersRes = await api.get(url);
        setWelders(weldersRes.data || []);
        
        // If technician_id is in URL, fetch that welder's details
        if (technician_id) {
          const welderRes = await api.get(`/lms/welders/${technician_id}`);
          setSelectedWelder(welderRes.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load welder data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [api, selectedFacility, statusFilter, technician_id]);

  const handleWelderClick = async (welder) => {
    try {
      const response = await api.get(`/lms/welders/${welder.technician_id}`);
      setSelectedWelder(response.data);
      navigate(`/training/welder/${welder.technician_id}`);
    } catch (error) {
      toast.error('Failed to load welder details');
    }
  };

  const handleBack = () => {
    setSelectedWelder(null);
    // UAT 2026-05-29: The legacy welder landing was retired in favor of the
    // Welding Personnel module. Always return the user to the new detail
    // page for this welder, or the summary list as a fallback.
    if (technician_id) {
      navigate(`/training/welding-personnel/${technician_id}`);
    } else {
      navigate('/training/welding-personnel');
    }
  };
  
  // Bug B fix: Refresh welders list when a log is added/updated
  const fetchWelders = async () => {
    try {
      let url = `/lms/welders?status_filter=${statusFilter}`;
      if (selectedFacility) {
        url += `&facility_id=${selectedFacility}`;
      }
      const weldersRes = await api.get(url);
      setWelders(weldersRes.data || []);
    } catch (error) {
      console.error('Error refreshing welders:', error);
    }
  };
  
  // Bug B fix: Callback when welder data is updated
  const handleWelderUpdate = (updatedWelder) => {
    // Update the welder in the list
    setWelders(prev => prev.map(w => 
      w.technician_id === updatedWelder.technician_id 
        ? { ...w, current_due_date: updatedWelder.current_due_date, days_remaining: updatedWelder.days_remaining, status_color: updatedWelder.status_color }
        : w
    ));
  };

  // Filter welders by search
  const filteredWelders = welders.filter(w => {
    const searchLower = searchQuery.toLowerCase();
    return (
      w.technician_name?.toLowerCase().includes(searchLower) ||
      w.technician_employee_id?.toLowerCase().includes(searchLower) ||
      w.facility_name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
      </div>
    );
  }

  // Show welder detail view
  if (selectedWelder) {
    return (
      <div className="p-6 max-w-7xl mx-auto" data-testid="welder-detail-page">
        <WelderDetail 
          welder={selectedWelder} 
          onBack={handleBack}
          api={api}
          user={user}
          onWelderUpdate={handleWelderUpdate}
        />
      </div>
    );
  }

  // Show welder list
  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="welder-maintenance-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
            <Flame className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Welder Maintenance</h1>
            <p className="text-sm text-gray-500">6-month recurring maintenance tracking</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, ID, or facility..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="welder-search"
                />
              </div>
            </div>
            {/* Bug E: Status filter */}
            <div className="w-full md:w-40">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-10 px-3 border rounded-md text-sm"
                data-testid="status-filter"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="all">All</option>
              </select>
            </div>
            {facilities.length > 1 && (
              <div className="w-full md:w-64">
                <select
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.target.value)}
                  className="w-full h-10 px-3 border rounded-md text-sm"
                  data-testid="facility-filter"
                >
                  <option value="">All Facilities</option>
                  {facilities.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Welder List */}
      <Card>
        <CardContent className="p-0">
          {filteredWelders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Flame className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">No welders found</p>
              <p className="text-sm">
                {searchQuery 
                  ? 'Try adjusting your search criteria'
                  : statusFilter === 'inactive'
                    ? 'No inactive welders in the system'
                    : 'Technicians with welder qualifications will appear here'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Welder Name</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Technician ID</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Facility</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Due Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWelders.map((welder) => (
                    <tr 
                      key={welder.technician_id}
                      onClick={() => handleWelderClick(welder)}
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      data-testid={`welder-row-${welder.technician_id}`}
                    >
                      <td className="py-3 px-4 font-medium">
                        <div className="flex items-center gap-2">
                          {welder.technician_name}
                          {welder.is_active === false && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-gray-200 text-gray-600">Inactive</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-600">{welder.technician_employee_id || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          {welder.facility_name}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {welder.current_due_date 
                          ? formatLogDate(welder.current_due_date)
                          : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        <StatusBadge 
                          color={welder.status_color} 
                          daysRemaining={welder.days_remaining}
                          isActive={welder.is_active !== false}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
